/**
 * useGlobalSearch Hook
 * 
 * Centralized hook for global search functionality.
 * Handles debouncing, API calls, and loading states for all sections.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  getAssets,
  getLiabilities,
  getEquities,
  getRevenues,
  getExpenses,
  LineItem,
} from '../../../services/financialStatementService';
import {
  getCycles,
  getProcesses,
  parseBackendCycles,
  parseBackendProcesses,
  Cycle,
  Process,
} from '../../../services/hierarchyService';
import {
  getRiskAssessmentProcesses,
  getObjectives,
  parseObjectives,
  RiskAssessmentProcessBackend,
  Objective,
} from '../../../services/riskAssessmentService';
import {
  getControlActivities,
  ControlActivity,
} from '../../../services/controlActivitiesService';
import {
  getAllGaps,
  Gap,
} from '../../../services/gapService';
import {
  getCOSOComplianceNotes,
  getCOSOComplianceDocuments,
  ParsedCOSONote,
  ParsedCOSODocument,
} from '../../../services/cosoComplianceService';
import {
  getProcessOwners,
  getProcessCertificationsForOwner,
  getCertificationDocuments,
  ProcessOwner,
  ProcessCertification,
  CertificationDocument,
} from '../../../services/certificationService';
import {
  getPlanningDocuments,
  getApplications,
  getChangeIndicators,
  getCertificationMessages,
  PlanningDocument,
  Application,
  ChangeIndicator,
  CertificationMessage,
} from '../../../services/auditPlanService';
import { buildSearchParams } from '../../../services/types';

// ========================================
// Constants
// ========================================

const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;

// ========================================
// Types
// ========================================

export interface FinancialStatementsResult {
  assets: LineItem[];
  liabilities: LineItem[];
  equity: LineItem[];
  revenue: LineItem[];
  expenses: LineItem[];
}

export interface CycleWithProcesses extends Cycle {
  processes: Process[];
}

export interface ProcessHierarchyResult {
  cycles: CycleWithProcesses[];
}

export interface AssessmentWithObjectives {
  assessment: RiskAssessmentProcessBackend;
  objectives: Objective[];
}

export interface RiskAssessmentResult {
  assessments: AssessmentWithObjectives[];
}

export interface COSOComplianceResult {
  notes: ParsedCOSONote[];
  documents: ParsedCOSODocument[];
}

export interface ProcessOwnerCertificationData {
  processOwner: ProcessOwner;
  certifications: ProcessCertification[];
  documents: CertificationDocument[];
}

export interface CertificationResult {
  processOwners: ProcessOwnerCertificationData[];
}

export interface AuditPlanResult {
  documents: PlanningDocument[];
  applications: Application[];
  changeIndicators: ChangeIndicator[];
  certificationMessages: CertificationMessage[];
}

export interface SearchResults {
  financialStatements: FinancialStatementsResult | null;
  processHierarchy: ProcessHierarchyResult | null;
  riskAssessment: RiskAssessmentResult | null;
  controlActivities: ControlActivity[] | null;
  gaps: Gap[] | null;
  cosoCompliance: COSOComplianceResult | null;
  certification: CertificationResult | null;
  auditPlan: AuditPlanResult | null;
}

export interface UseGlobalSearchReturn {
  // Input handling
  inputText: string;
  setInputText: (text: string) => void;
  
  // Search state
  searchText: string;
  isLoading: boolean;
  
  // Results
  results: SearchResults;
  hasAnyResults: boolean;
  
  // Clear search
  clearSearch: () => void;
}

// ========================================
// Hook
// ========================================

export const useGlobalSearch = (): UseGlobalSearchReturn => {
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { user } = useSelector((state: RootState) => state.auth);
  const annualReportId = selectedReport?.annualReportId;
  const financialStatementId = selectedReport?.financialStatementId;
  const companyId = user?.companyId;
  
  // Input and search text
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  
  // Loading states
  const [isLoadingFinancialStatements, setIsLoadingFinancialStatements] = useState(false);
  const [isLoadingProcessHierarchy, setIsLoadingProcessHierarchy] = useState(false);
  const [isLoadingRiskAssessment, setIsLoadingRiskAssessment] = useState(false);
  const [isLoadingControlActivities, setIsLoadingControlActivities] = useState(false);
  const [isLoadingGaps, setIsLoadingGaps] = useState(false);
  const [isLoadingCOSOCompliance, setIsLoadingCOSOCompliance] = useState(false);
  const [isLoadingCertification, setIsLoadingCertification] = useState(false);
  const [isLoadingAuditPlan, setIsLoadingAuditPlan] = useState(false);
  
  // Results
  const [results, setResults] = useState<SearchResults>({
    financialStatements: null,
    processHierarchy: null,
    riskAssessment: null,
    controlActivities: null,
    gaps: null,
    cosoCompliance: null,
    certification: null,
    auditPlan: null,
  });
  
  // Refs
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentSearchTermRef = useRef<string>(''); // Track current search term to ignore stale results
  
  // Handle input change with debounce
  const handleInputChange = useCallback((text: string) => {
    setInputText(text);
    
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // If empty or too short, clear immediately
    if (!text.trim() || text.trim().length < MIN_SEARCH_LENGTH) {
      setSearchText('');
      setResults({
        financialStatements: null,
        processHierarchy: null,
        riskAssessment: null,
        controlActivities: null,
        gaps: null,
        cosoCompliance: null,
        certification: null,
        auditPlan: null,
      });
      return;
    }
    
    // Debounce the actual search
    debounceTimerRef.current = setTimeout(() => {
      setSearchText(text.trim());
    }, SEARCH_DEBOUNCE_MS);
  }, []);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setInputText('');
    setSearchText('');
    setResults({
      financialStatements: null,
      processHierarchy: null,
      riskAssessment: null,
      controlActivities: null,
      gaps: null,
      cosoCompliance: null,
      certification: null,
      auditPlan: null,
    });
    
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);
  
  // ========================================
  // EX2 - Fetch Financial Statements
  // ========================================
  const fetchFinancialStatements = useCallback(async (term: string) => {
    if (!financialStatementId || !term) return;
    
    setIsLoadingFinancialStatements(true);
    
    try {
      const request = {
        requiresCounts: true,
        skip: 0,
        take: 50,
        search: buildSearchParams(term),
      };
      
      const [assetsRes, liabilitiesRes, equitiesRes, revenuesRes, expensesRes] = 
        await Promise.allSettled([
          getAssets(financialStatementId, request),
          getLiabilities(financialStatementId, request),
          getEquities(financialStatementId, request),
          getRevenues(financialStatementId, request),
          getExpenses(financialStatementId, request),
        ]);
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      const result: FinancialStatementsResult = {
        assets: assetsRes.status === 'fulfilled' ? assetsRes.value.result : [],
        liabilities: liabilitiesRes.status === 'fulfilled' ? liabilitiesRes.value.result : [],
        equity: equitiesRes.status === 'fulfilled' ? equitiesRes.value.result : [],
        revenue: revenuesRes.status === 'fulfilled' ? revenuesRes.value.result : [],
        expenses: expensesRes.status === 'fulfilled' ? expensesRes.value.result : [],
      };
      
      setResults(prev => ({ ...prev, financialStatements: result }));
    } catch (error) {
      // Check if search term changed - don't set error state for stale requests
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching financial statements:', error);
      setResults(prev => ({
        ...prev,
        financialStatements: { assets: [], liabilities: [], equity: [], revenue: [], expenses: [] },
      }));
    } finally {
      // Only clear loading if this is still the current search
      if (term === currentSearchTermRef.current) {
        setIsLoadingFinancialStatements(false);
      }
    }
  }, [financialStatementId]);
  
  // ========================================
  // EX3 - Fetch Process Hierarchy
  // ========================================
  const fetchProcessHierarchy = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingProcessHierarchy(true);
    
    try {
      // First get all cycles (without search filter)
      const cyclesResponse = await getCycles(annualReportId, {
        requiresCounts: true,
        skip: 0,
        take: 100,
      });
      const cycles = parseBackendCycles(cyclesResponse.result);
      
      // For each cycle, search for processes with the search term
      const cyclesWithProcesses: CycleWithProcesses[] = [];
      
      await Promise.all(
        cycles.map(async (cycle) => {
          try {
            const processesResponse = await getProcesses(cycle.id, {
              requiresCounts: true,
              skip: 0,
              take: 100,
              search: buildSearchParams(term),
            });
            const processes = parseBackendProcesses(processesResponse.result, cycle.id);
            
            if (processes.length > 0) {
              cyclesWithProcesses.push({ ...cycle, processes });
            }
          } catch (error) {
            console.error(`Error fetching processes for cycle ${cycle.id}:`, error);
          }
        })
      );
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      // Sort by cycle code
      cyclesWithProcesses.sort((a, b) => a.cycleCode.localeCompare(b.cycleCode));
      
      setResults(prev => ({ ...prev, processHierarchy: { cycles: cyclesWithProcesses } }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching process hierarchy:', error);
      setResults(prev => ({ ...prev, processHierarchy: { cycles: [] } }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingProcessHierarchy(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // EX4 - Fetch Risk Assessment
  // ========================================
  const fetchRiskAssessment = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingRiskAssessment(true);
    
    try {
      // First get all risk assessments
      const assessmentsResponse = await getRiskAssessmentProcesses(annualReportId);
      const assessments = assessmentsResponse.data || [];
      
      // For each assessment, search for objectives
      const assessmentsWithObjectives: AssessmentWithObjectives[] = [];
      
      await Promise.all(
        assessments.map(async (assessment) => {
          try {
            const objectivesResponse = await getObjectives(assessment.assessmentId, {
              skip: 0,
              take: 100,
              requiresCounts: true,
              search: buildSearchParams(term),
            });
            const objectives = parseObjectives(objectivesResponse.result || []);
            
            if (objectives.length > 0) {
              assessmentsWithObjectives.push({ assessment, objectives });
            }
          } catch (error) {
            console.error(`Error fetching objectives for assessment ${assessment.assessmentId}:`, error);
          }
        })
      );
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      // Sort by process name
      assessmentsWithObjectives.sort((a, b) => 
        a.assessment.processName.localeCompare(b.assessment.processName)
      );
      
      setResults(prev => ({ ...prev, riskAssessment: { assessments: assessmentsWithObjectives } }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching risk assessment:', error);
      setResults(prev => ({ ...prev, riskAssessment: { assessments: [] } }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingRiskAssessment(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // EX5 - Fetch Control Activities
  // ========================================
  const fetchControlActivities = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingControlActivities(true);
    
    try {
      const response = await getControlActivities({
        annualReportId,
        search: buildSearchParams(term),
        sorted: [{ name: 'ControlCode', direction: 'ascending' }],
        take: 100,
      });
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      setResults(prev => ({ ...prev, controlActivities: response.data }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching control activities:', error);
      setResults(prev => ({ ...prev, controlActivities: [] }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingControlActivities(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // EX6 - Fetch Gaps
  // ========================================
  const fetchGaps = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingGaps(true);
    
    try {
      const response = await getAllGaps({
        annualReportId,
        search: buildSearchParams(term),
        sorted: [{ name: 'GapCode', direction: 'ascending' }],
        take: 100,
      });
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      setResults(prev => ({ ...prev, gaps: response.data }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching gaps:', error);
      setResults(prev => ({ ...prev, gaps: [] }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingGaps(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // EX8 - Fetch COSO Compliance
  // ========================================
  const fetchCOSOCompliance = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingCOSOCompliance(true);
    
    try {
      // Fetch both notes and documents in parallel
      const [notesResponse, documentsResponse] = await Promise.allSettled([
        getCOSOComplianceNotes(annualReportId, buildSearchParams(term)),
        getCOSOComplianceDocuments(annualReportId),
      ]);
      
      const notes = notesResponse.status === 'fulfilled' ? notesResponse.value.data : [];
      const allDocuments = documentsResponse.status === 'fulfilled' ? documentsResponse.value.data : [];
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      // Filter documents by search term (client-side since API doesn't support search)
      const searchLower = term.toLowerCase();
      const documents = allDocuments.filter(doc => 
        doc.fileName.toLowerCase().includes(searchLower)
      );
      
      setResults(prev => ({ ...prev, cosoCompliance: { notes, documents } }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching COSO compliance:', error);
      setResults(prev => ({ ...prev, cosoCompliance: { notes: [], documents: [] } }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingCOSOCompliance(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // EX7 - Fetch Certification
  // ========================================
  const fetchCertification = useCallback(async (term: string) => {
    if (!annualReportId || !companyId || !term) return;
    
    setIsLoadingCertification(true);
    
    try {
      // First get all process owners
      const processOwners = await getProcessOwners(companyId);
      
      // For each process owner, fetch certifications and documents
      const processOwnerDataList: ProcessOwnerCertificationData[] = [];
      
      await Promise.all(
        processOwners.map(async (processOwner) => {
          try {
            // Fetch certifications with search
            const [certificationsResult, documentsResult] = await Promise.allSettled([
              getProcessCertificationsForOwner(annualReportId, processOwner.id, buildSearchParams(term)),
              getCertificationDocuments(annualReportId, processOwner.id),
            ]);
            
            const certifications = certificationsResult.status === 'fulfilled' 
              ? certificationsResult.value 
              : [];
            const allDocuments = documentsResult.status === 'fulfilled' 
              ? documentsResult.value 
              : [];
            
            // Filter documents by search term (client-side)
            const searchLower = term.toLowerCase();
            const documents = allDocuments.filter(doc => 
              doc.fileName.toLowerCase().includes(searchLower)
            );
            
            // Only include if there are matching results
            if (certifications.length > 0 || documents.length > 0) {
              processOwnerDataList.push({
                processOwner,
                certifications,
                documents,
              });
            }
          } catch (error) {
            console.error(`Error fetching certification data for process owner ${processOwner.id}:`, error);
          }
        })
      );
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      // Sort by process owner name
      processOwnerDataList.sort((a, b) => 
        a.processOwner.processOwnerName.localeCompare(b.processOwner.processOwnerName)
      );
      
      setResults(prev => ({ ...prev, certification: { processOwners: processOwnerDataList } }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching certification:', error);
      setResults(prev => ({ ...prev, certification: { processOwners: [] } }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingCertification(false);
      }
    }
  }, [annualReportId, companyId]);
  
  // ========================================
  // EX9 - Fetch Audit Plan
  // ========================================
  const fetchAuditPlan = useCallback(async (term: string) => {
    if (!annualReportId || !term) return;
    
    setIsLoadingAuditPlan(true);
    
    try {
      // Fetch all 4 sub-sections in parallel
      const [documentsRes, applicationsRes, changeIndicatorsRes, certMessagesRes] = 
        await Promise.allSettled([
          getPlanningDocuments({ annualReportId }),
          getApplications({ annualReportId, search: buildSearchParams(term) }),
          getChangeIndicators({ annualReportId }),
          getCertificationMessages({ annualReportId }),
        ]);
      
      // Get all results
      const allDocuments = documentsRes.status === 'fulfilled' ? documentsRes.value.data : [];
      const allApplications = applicationsRes.status === 'fulfilled' ? applicationsRes.value.data : [];
      const allChangeIndicators = changeIndicatorsRes.status === 'fulfilled' ? changeIndicatorsRes.value.data : [];
      const allCertMessages = certMessagesRes.status === 'fulfilled' ? certMessagesRes.value.data : [];
      
      // Filter documents, change indicators, and certification messages client-side 
      // (these APIs don't support search)
      const searchLower = term.toLowerCase();
      
      const filteredDocuments = allDocuments.filter(doc => 
        doc.fileName.toLowerCase().includes(searchLower) ||
        doc.uploadedBy.toLowerCase().includes(searchLower)
      );
      
      const filteredChangeIndicators = allChangeIndicators.filter(ci => 
        ci.processOwnerName.toLowerCase().includes(searchLower) ||
        ci.question.toLowerCase().includes(searchLower) ||
        ci.response.toLowerCase().includes(searchLower)
      );
      
      const filteredCertMessages = allCertMessages.filter(cm => 
        cm.processOwnerName.toLowerCase().includes(searchLower) ||
        cm.section.toLowerCase().includes(searchLower) ||
        cm.message.toLowerCase().includes(searchLower)
      );
      
      // Check if search term changed while fetching - ignore stale results
      if (term !== currentSearchTermRef.current) return;
      
      setResults(prev => ({
        ...prev,
        auditPlan: {
          documents: filteredDocuments,
          applications: allApplications, // Already filtered by API
          changeIndicators: filteredChangeIndicators,
          certificationMessages: filteredCertMessages,
        },
      }));
    } catch (error) {
      if (term !== currentSearchTermRef.current) return;
      console.error('Error fetching audit plan:', error);
      setResults(prev => ({
        ...prev,
        auditPlan: {
          documents: [],
          applications: [],
          changeIndicators: [],
          certificationMessages: [],
        },
      }));
    } finally {
      if (term === currentSearchTermRef.current) {
        setIsLoadingAuditPlan(false);
      }
    }
  }, [annualReportId]);
  
  // ========================================
  // Effect to trigger search when searchText changes
  // ========================================
  useEffect(() => {
    // Update the current search term ref (used to ignore stale results)
    currentSearchTermRef.current = searchText;
    
    if (!searchText) {
      return;
    }
    
    // Cancel previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    // Start all searches in parallel
    fetchFinancialStatements(searchText);
    fetchProcessHierarchy(searchText);
    fetchRiskAssessment(searchText);
    fetchControlActivities(searchText);
    fetchGaps(searchText);
    fetchCOSOCompliance(searchText);
    fetchCertification(searchText);
    fetchAuditPlan(searchText);
    
  }, [searchText, fetchFinancialStatements, fetchProcessHierarchy, fetchRiskAssessment, fetchControlActivities, fetchGaps, fetchCOSOCompliance, fetchCertification, fetchAuditPlan]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // ========================================
  // Calculate derived states
  // ========================================
  const isLoading = isLoadingFinancialStatements || isLoadingProcessHierarchy || 
                    isLoadingRiskAssessment || isLoadingControlActivities || 
                    isLoadingGaps || isLoadingCOSOCompliance || isLoadingCertification ||
                    isLoadingAuditPlan;
  
  const hasAnyResults = (() => {
    // EX2 - Financial Statements
    const fs = results.financialStatements;
    if (fs) {
      const fsTotal = fs.assets.length + fs.liabilities.length + fs.equity.length + 
                      fs.revenue.length + fs.expenses.length;
      if (fsTotal > 0) return true;
    }
    
    // EX3 - Process Hierarchy
    if (results.processHierarchy && results.processHierarchy.cycles.length > 0) {
      return true;
    }
    
    // EX4 - Risk Assessment
    if (results.riskAssessment && results.riskAssessment.assessments.length > 0) {
      return true;
    }
    
    // EX5 - Control Activities
    if (results.controlActivities && results.controlActivities.length > 0) {
      return true;
    }
    
    // EX6 - Gaps
    if (results.gaps && results.gaps.length > 0) {
      return true;
    }
    
    // EX7 - Certification
    if (results.certification && results.certification.processOwners.length > 0) {
      return true;
    }
    
    // EX8 - COSO Compliance
    if (results.cosoCompliance) {
      if (results.cosoCompliance.notes.length > 0 || results.cosoCompliance.documents.length > 0) {
        return true;
      }
    }
    
    // EX9 - Audit Plan
    if (results.auditPlan) {
      const ap = results.auditPlan;
      if (ap.documents.length > 0 || ap.applications.length > 0 || 
          ap.changeIndicators.length > 0 || ap.certificationMessages.length > 0) {
        return true;
      }
    }
    
    return false;
  })();
  
  return {
    inputText,
    setInputText: handleInputChange,
    searchText,
    isLoading,
    results,
    hasAnyResults,
    clearSearch,
  };
};

export default useGlobalSearch;
